<script setup lang="ts">
import { getTranslations } from '@/lib/util/i18n';
import { CoButton } from '@deputy/copilot2';
import { ResetPinMode } from '@/lib/request/resetPin';

type ResetPinModeConfig = {
  name: string;
  mode: ResetPinMode;
  icon: string;
};

const props = withDefaults(
  defineProps<{
    email: string;
    mobile: string;
    modes?: ResetPinModeConfig[];
  }>(),
  {
    modes: () => [
      { name: 'email', mode: ResetPinMode.Email, icon: 'email' },
      { name: 'sms', mode: ResetPinMode.SMS, icon: 'phone' },
    ],
  }
);

const emit = defineEmits(['select', 'back']);

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'reset-pin-method',
});

function obscureUserInformation(information: string, mode: string) {
  const anonymousText = mode === 'email' ? '**@**' : '******';
  return information.substring(0, 3) + anonymousText;
}

const hasContactDetails = props.email || props.mobile;
</script>

<template>
  <div class="reset-pin-method">
    <CoButton class="__back" @click="$emit('back')" data-test-id="back">
      <img src="../assets/images/arrow.png" />
    </CoButton>

    <h1>{{ t(hasContactDetails ? 'select-option' : 'no-option') }}</h1>

    <div class="__buttons-container" v-if="hasContactDetails">
      <div class="__buttons" v-for="mode in modes" :key="mode.mode">
        <CoButton
          @click="emit('select', mode.mode)"
          type="text"
          class="--reset"
          :data-test-id="`reset-mode-${mode.mode}`"
          v-if="
            (`${mode.name}` == 'email' && email) ||
            (`${mode.name}` == 'sms' && mobile)
          "
        >
          <div class="__reset-icon-container">
            <co-icon :name="mode.icon" />
          </div>
          <span>
            {{
              `${mode.name}` == 'email'
                ? t('email-button', {
                    email: obscureUserInformation(email, 'email'),
                  })
                : t('sms-button', {
                    mobile: obscureUserInformation(mobile, 'sms'),
                  })
            }}
          </span>
        </CoButton>
      </div>
    </div>
    <div v-else>
      <p>
        {{ t('no-option-action') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.reset-pin-method {
  display: flex;
  flex-direction: column;
  align-items: start;
  color: var(--co-text-primary);
}

p {
  font-size: 16px;
}

h1 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 2rem;
}

.__back {
  border: none;
  background: none;
  cursor: pointer;
  padding: 20px;
  margin: -20px 0 15px -20px;
}

.__back img {
  width: 16px;
}

.__buttons-container,
.__buttons {
  width: 100%;
}

.--reset.co-button {
  padding: 0;
  margin: 0;
  margin-bottom: 1rem;
  color: var(--co-text-primary);
  font-size: 1rem;
  text-align: start;
  width: 100%;
  --icon-size: 40px;
  --icon-margin: 1rem;
}
.--reset span {
  vertical-align: text-top;
}
.--reset::after {
  content: '';
  display: block;
  margin-left: calc(var(--icon-size) + var(--icon-margin));
  border-bottom: 2px solid var(--co-button-secondary-fill);
  margin-right: 10px;
}

.__reset-icon-container {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: var(--icon-size);
  height: var(--icon-size);
  border-radius: 50%;
  color: var(--co-text-highlight);
  background-color: var(--co-button-secondary-fill);
  margin-right: var(--icon-margin);
}
</style>
